import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide"
import "@splidejs/react-splide/css"
import Image from "components/elements/image"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"
import { formatAddressJSX } from "utils/formatAddress"

import "./carousel.scss"

const Carousel = ({ featuredLocations }) => {
  const imagesRef = useRef(null)
  const detailsRef = useRef(null)

  useEffect(() => {
    if (imagesRef.current && detailsRef.current) {
      // Sync details slider with images slider
      imagesRef.current.sync(detailsRef.current.splide)

      const splide = imagesRef.current.splide
      if (!splide) return

      const list = () => splide.Components?.Pagination?.list

      const updateLisFromButtons = () => {
        const ul = list()
        if (!ul) return
        const buttons = ul.querySelectorAll(".splide__pagination__page")
        buttons.forEach(btn => {
          const li = btn.parentElement
          if (!li) return
          li.classList.toggle("is-active", btn.classList.contains("is-active"))
        })
      }

      // Initial and event-based sync
      splide.on("mounted", updateLisFromButtons)
      splide.on("move", updateLisFromButtons)
      splide.on("moved", updateLisFromButtons)
      splide.on("pagination:mounted", updateLisFromButtons)
      splide.on("pagination:updated", updateLisFromButtons)

      // MutationObserver as a fallback if Splide toggles classes without events
      const ul = list()
      let observer
      if (ul) {
        observer = new MutationObserver(updateLisFromButtons)
        observer.observe(ul, {
          attributes: true,
          subtree: true,
          attributeFilter: ["class"],
        })
        // Run once on mount
        updateLisFromButtons()
      }

      // Cleanup
      return () => {
        if (observer) observer.disconnect()
      }
    }
  }, [])

  return (
    <div className="location-section-v2--carousel">
      {/* Images slider with pagination (dots) */}
      <Splide
        aria-label="Featured Locations Images"
        hasTrack={false}
        options={{
          type: "fade",
          arrows: false,
          autoplay: true,
          pauseOnHover: true,
          rewind: true,
          interval: 5000,
          gap: "16px",
          pagination: true,
          perMove: 1,
          perPage: 1,
          speed: 300,
          easing: "ease-in-out",
        }}
        ref={imagesRef}
      >
        <div className="custom-wrapper">
          <SplideTrack>
            {featuredLocations?.length > 0 &&
              featuredLocations.map(location => (
                <SplideSlide key={location.id}>
                  <Image
                    alt={location.name}
                    className="location-section-v2--carousel-image"
                    image={location.image.fluid}
                  />
                </SplideSlide>
              ))}
          </SplideTrack>
        </div>
      </Splide>

      {/* Details slider (synced), with no pagination or arrows */}
      <Splide
        aria-label="Featured Locations Details"
        options={{
          type: "fade",
          arrows: false,
          drag: true,
          autoplay: true,
          pauseOnHover: true,
          rewind: true,
          interval: 5000,
          pagination: false,
          perMove: 1,
          perPage: 1,
          speed: 250,
          easing: "ease-in-out",
        }}
        ref={detailsRef}
      >
        {featuredLocations?.length > 0 &&
          featuredLocations.map(location => (
            <SplideSlide key={`details-${location.id}`}>
              <div className="location-section-v2--location-details">
                <h3 className="title">{location.name}</h3>
                <div className="address-container">
                  <p className="address">
                    {formatAddressJSX(location.detailedAddress)}
                  </p>
                </div>
              </div>
            </SplideSlide>
          ))}
      </Splide>
    </div>
  )
}

Carousel.propTypes = {
  featuredLocations: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default Carousel
